import { useReducer } from 'react';
import { useSearchParams } from 'react-router';

import type { components } from '~/api/openapi';

export type CommentPosition = {
  x: number;
  y: number;
};

type CommentState = {
  isCommentMode: boolean;
  activeCommentPosition: CommentPosition | null;
  pendingComment: CommentPosition | null;
};

type CommentAction =
  | { type: 'TOGGLE_COMMENT_MODE' }
  | { type: 'START_CREATING_COMMENT'; position: CommentPosition }
  | { type: 'VIEW_COMMENTS'; position: CommentPosition }
  | { type: 'CLOSE_PENDING_COMMENT' }
  | { type: 'COMMENT_GROUP_COUNT_CHANGED' }
  | { type: 'CLOSE_ACTIVE_COMMENTS' };

function commentReducer(state: CommentState, action: CommentAction): CommentState {
  switch (action.type) {
    case 'COMMENT_GROUP_COUNT_CHANGED':
      return {
        ...state,
        isCommentMode: false,
        activeCommentPosition: null,
        pendingComment: null,
      };
    case 'TOGGLE_COMMENT_MODE':
      return {
        ...state,
        isCommentMode: !state.isCommentMode,
        activeCommentPosition: null,
        pendingComment: null,
      };
    case 'START_CREATING_COMMENT':
      return {
        ...state,
        pendingComment: state.pendingComment ? null : action.position, // hide pendingComment if it's present to act as dismiss
      };
    case 'VIEW_COMMENTS':
      return {
        ...state,
        activeCommentPosition: action.position,
        isCommentMode: false,
        pendingComment: null,
      };
    case 'CLOSE_PENDING_COMMENT':
      return {
        ...state,
        pendingComment: null,
        isCommentMode: false,
      };
    case 'CLOSE_ACTIVE_COMMENTS':
      return {
        ...state,
        activeCommentPosition: null,
      };
    default:
      return state;
  }
}

const initialCommentState: CommentState = {
  isCommentMode: true,
  activeCommentPosition: null,
  pendingComment: null,
};

const POS_X_NAME = 'commentX';
const POS_Y_NAME = 'commentY';
export function useCommentState() {
  const [searchQuery] = useSearchParams();
  const xRaw = searchQuery.get(POS_X_NAME);
  const yRaw = searchQuery.get(POS_Y_NAME);
  const x = xRaw !== null ? Number(xRaw) : undefined;
  const y = yRaw !== null ? Number(yRaw) : undefined;
  if (x && y && !isNaN(x) && !isNaN(y)) {
    initialCommentState.activeCommentPosition = { x, y };
  }

  const [state, dispatch] = useReducer(commentReducer, initialCommentState);

  return {
    state,
    dispatch,
    isCommentMode: state.isCommentMode,
    activeCommentPosition: state.activeCommentPosition,
    pendingComment: state.pendingComment,
  };
}

type Comment = components['schemas']['SubmissionCommentWithEmojiDto'];
export function generateCommentQueryParam(comment: Partial<Comment>) {
  // TODO: fix the type after BE update the API dto
  const { x, y } = comment.position as unknown as { x: number; y: number };
  if (!isNaN(x) && !isNaN(y)) {
    return new URLSearchParams([
      [POS_X_NAME, x.toString()],
      [POS_Y_NAME, y.toString()],
    ]);
  }
}
